import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

interface SpotlightProps {
  queryParam: string;
  targetSelector: string;
  duration?: number;
  delay?: number;
  multiple?: boolean;
}

/**
 * Spotlight component highlights specific DOM elements with a darkened overlay.
 *
 * WHY WE KEEP BOTH SPOTLIGHT AND JOYRIDE:
 * - Joyride is excellent for guided tours with tooltips and step-by-step navigation
 * - Spotlight is purpose-built for pure element highlighting without any UI chrome
 * - Joyride's spotlight feature is tightly coupled with tooltip display and doesn't work well
 *   for simple highlighting use cases (requires complex workarounds to hide tooltips)
 * - This component provides a clean, lightweight solution for highlighting elements
 *   triggered by URL query parameters, perfect for onboarding checklists
 *
 * WHY THE COMPLEXITY:
 * - Elements may not exist immediately after navigation (async rendering)
 * - Elements may exist but have no dimensions yet (still laying out)
 * - Multiple attempts are needed to catch dynamically rendered content
 * - Two separate effects prevent infinite loops and race conditions
 */
export function Spotlight({
  queryParam,
  targetSelector,
  duration = 5000,
  delay = 500,
  multiple = false,
}: SpotlightProps) {
  const { search, pathname } = useLocation();
  const navigate = useNavigate();
  const [spotlights, setSpotlights] = useState<DOMRect[]>([]);
  const [isHighlighting, setIsHighlighting] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout>();
  const elementsRef = useRef<Element[]>([]);

  // Effect 1: Detect query parameter and trigger highlighting
  // Separated from the main logic to avoid re-running on every state change
  useEffect(() => {
    const params = new URLSearchParams(search);
    if (params.get(queryParam) === 'true') {
      // Set highlighting state to trigger the second effect
      setIsHighlighting(true);

      // Clean the URL immediately to prevent re-triggering on back/forward navigation
      params.delete(queryParam);
      const newSearch = params.toString();
      navigate(`${pathname}${newSearch ? `?${newSearch}` : ''}`, {
        replace: true,
      });
    }
  }, [search, pathname, navigate, queryParam, targetSelector]);

  // Effect 2: Handle the actual highlighting logic
  // Runs when isHighlighting becomes true
  useEffect(() => {
    if (isHighlighting) {
      let retryCount = 0;
      const maxRetries = 10; // Try up to 10 times (2 seconds total) to find elements

      const tryFindElement = () => {
        let elements;
        if (multiple) {
          elements = document.querySelectorAll(targetSelector);
        } else {
          // For single elements, find ALL matching elements and pick the first visible one
          // This handles cases where multiple elements match but only one is visible
          const allElements = document.querySelectorAll(targetSelector);
          const visibleElement = Array.from(allElements).find(el => {
            const rect = el.getBoundingClientRect();
            return rect.width > 0 && rect.height > 0;
          });
          elements = visibleElement ? [visibleElement] : [];
        }

        if (elements.length > 0) {
          const rects: DOMRect[] = [];
          let hasValidElement = false;

          // Filter out elements with no dimensions (still rendering/hidden)
          elements.forEach(el => {
            if (el) {
              const rect = el.getBoundingClientRect();
              if (rect.width > 0 && rect.height > 0) {
                rects.push(rect);
                hasValidElement = true;
              }
            }
          });

          if (hasValidElement) {
            // Success! Elements found and have dimensions
            elementsRef.current = Array.from(elements);
            setSpotlights(rects);

            // Use requestAnimationFrame to ensure DOM is ready for animation
            requestAnimationFrame(() => {
              setIsVisible(true);
            });

            // Auto-dismiss after specified duration
            timeoutRef.current = setTimeout(() => {
              setIsVisible(false);
              setTimeout(() => {
                setSpotlights([]);
                setIsHighlighting(false);
              }, 150); // Wait for fade-out animation to complete
            }, duration);
          } else if (retryCount < maxRetries) {
            // Elements exist but have no dimensions yet - retry
            // This happens when elements are in DOM but still laying out
            retryCount++;
            setTimeout(tryFindElement, 200);
          } else {
            // Give up after max retries
            setIsHighlighting(false);
          }
        } else if (retryCount < maxRetries) {
          // No elements found yet - retry
          // This handles async rendering where elements appear after navigation
          retryCount++;
          setTimeout(tryFindElement, 200);
        } else {
          // No elements found after all retries - give up silently
          setIsHighlighting(false);
        }
      };

      // Start trying after initial delay to let page settle
      const setupTimeout = setTimeout(tryFindElement, delay);

      // Allow user to dismiss by clicking anywhere
      const handleClick = () => {
        setIsVisible(false);
        setTimeout(() => {
          setSpotlights([]);
          setIsHighlighting(false);
          elementsRef.current = [];
        }, 150); // Wait for fade-out animation
      };

      // Update spotlight positions on scroll
      const handleScroll = () => {
        if (elementsRef.current.length > 0) {
          requestAnimationFrame(() => {
            const newRects: DOMRect[] = [];
            elementsRef.current.forEach(el => {
              if (el) {
                const rect = el.getBoundingClientRect();
                newRects.push(rect);
              }
            });
            setSpotlights(newRects);
          });
        }
      };

      document.addEventListener('click', handleClick);
      window.addEventListener('scroll', handleScroll, true); // Use capture to catch all scroll events

      // Cleanup function
      return () => {
        clearTimeout(setupTimeout);
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }
        document.removeEventListener('click', handleClick);
        window.removeEventListener('scroll', handleScroll, true);
      };
    }
    return undefined;
  }, [isHighlighting, targetSelector, duration, delay, multiple]);

  if (!isHighlighting || spotlights.length === 0) {
    return null;
  }

  const maskId = `spotlight-mask-${queryParam}`;

  return (
    <>
      {/* SVG with mask for cutout */}
      <svg
        data-testid="spotlight-overlay"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          zIndex: 1040,
          pointerEvents: 'none',
          opacity: isVisible ? 1 : 0,
          transition: 'opacity 0.15s ease-out',
        }}
      >
        <defs>
          <mask id={maskId}>
            {/* White background = visible */}
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {/* Black rectangles = transparent cutouts */}
            {spotlights.map((rect, index) => (
              <rect
                key={index}
                x={rect.left - 8}
                y={rect.top - 8}
                width={rect.width + 16}
                height={rect.height + 16}
                rx="12"
                ry="12"
                fill="black"
              />
            ))}
          </mask>
        </defs>
        {/* Dark overlay with mask applied */}
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.5)"
          mask={`url(#${maskId})`}
          style={{ pointerEvents: 'auto' }}
        />
      </svg>

      {/* White borders around spotlights */}
      {spotlights.map((rect, index) => (
        <div
          key={index}
          style={{
            position: 'fixed',
            top: rect.top - 8,
            left: rect.left - 8,
            width: rect.width + 16,
            height: rect.height + 16,
            borderRadius: '12px',
            border: '2px solid rgba(255, 255, 255, 0.3)',
            zIndex: 1041,
            pointerEvents: 'none',
            opacity: isVisible ? 1 : 0,
            transition: 'opacity 0.15s ease-out, transform 0.15s ease-out',
            transform: isVisible ? 'scale(1)' : 'scale(0.97)',
            animationDelay: multiple ? `${index * 0.02}s` : '0s',
          }}
        />
      ))}

      {/* Make the target element clickable */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
          ${targetSelector} {
            position: relative;
            z-index: 1042 !important;
          }
        `,
        }}
      />
    </>
  );
}
